import React, { useState, useCallback } from "react";
import { StyleSheet, ScrollView, TouchableOpacity } from "react-native";
import { object, string } from "@lib/util/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { View, Wrapper, Text, Card } from "@components/atoms/Themed";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import Toast from "@components/atoms/Toast";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import { graphql, useFragment, useMutation } from "react-relay/hooks";
import { SnsAssociationTiktok$key } from "@generated/SnsAssociationTiktok.graphql";
import { SnsAssociationTiktokMutation } from "@generated/SnsAssociationTiktokMutation.graphql";
import Spacer from "@components/atoms/Spacer";
import { KeyboardId, TiktokUrlPattern } from "@constants/App";
import GradientButton from "@components/atoms/GradientButton";
import Divider from "@components/atoms/Divider";
import { TextField, ErrorText } from "@components/molecules/TextInput";
import openURL from "@lib/util/openUrl";
import Icon from "@components/atoms/Icon";
import Colors from "@constants/Colors";
import TtgoCreatorSwitch from "@components/molecules/TtgoCreatorSwitch";
import { TtgoStatus } from "@components/atoms/TtgoStatusBadge";

const tiktokQuery = graphql`
  fragment SnsAssociationTiktok on Influencer {
    profile {
      tiktokUrl
      isTiktokGoCreator
      tiktokGoStatus
      tiktokGoUniqueId
    }
  }
`;

const applyTtgoMutationGraphql = graphql`
  mutation SnsAssociationTiktokApplyTtgoMutation(
    $input: ApplyTiktokGoCreatorMutationInput!
  ) {
    applyTiktokGoCreator(input: $input) {
      ... on Influencer {
        id
        profile {
          isTiktokGoCreator
          tiktokGoStatus
          tiktokGoUniqueId
        }
      }
      ... on UserError {
        message
      }
    }
  }
`;

const updateQuery = graphql`
  mutation SnsAssociationTiktokMutation(
    $input: UpdateInfluencerInfoMutationInput!
  ) {
    updateInfluencerInfo(input: $input) {
      __typename
      ... on Influencer {
        profile {
          tiktokUrl
        }
      }
      ... on UpdateInfluencerInfoError {
        message
      }
    }
  }
`;

type InputData = {
  tiktokUrl: string | null;
};

export default function SnsAssociationTiktok({
  tiktokFragment,
}: {
  tiktokFragment: SnsAssociationTiktok$key;
}) {
  const { profile } = useFragment(tiktokQuery, tiktokFragment);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commit] = useMutation<SnsAssociationTiktokMutation>(updateQuery);
  const [commitApplyTtgo] = useMutation(applyTtgoMutationGraphql);
  const [changed, setChanged] = useState<boolean>(false);

  // TTGOのローカル編集状態。トグル/ユニークIDを保存ボタン押下まで通信せず保持する。
  // 却下中は DB上 is_creator=true だが画面ではOFF扱いにする。
  const initialTtgoApply =
    profile.isTiktokGoCreator && profile.tiktokGoStatus !== "rejected";
  const initialTtgoUniqueId = profile.tiktokGoUniqueId ?? "";
  const [ttgoApply, setTtgoApply] = useState<boolean>(initialTtgoApply);
  const [ttgoUniqueId, setTtgoUniqueId] = useState<string>(initialTtgoUniqueId);

  const ttgoDirty =
    ttgoApply !== initialTtgoApply || ttgoUniqueId !== initialTtgoUniqueId;
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<InputData>({
    defaultValues: {
      tiktokUrl: profile.tiktokUrl,
    },
    mode: "all",
    resolver: yupResolver(
      object().shape({
        tiktokUrl: string()
          .default(null)
          .trim()
          .url("存在しないURLです")
          .matches(TiktokUrlPattern, "アカウントURLを入力してください")
          .nullable()
          .toNull(),
      })
    ),
  });

  const saveTtgo = useCallback(async (): Promise<boolean> => {
    if (!ttgoDirty) return true;
    return new Promise<boolean>((resolve) => {
      commitApplyTtgo({
        variables: {
          input: {
            apply: ttgoApply,
            tiktokGoUniqueId:
              ttgoApply && ttgoUniqueId.trim() !== ""
                ? ttgoUniqueId.trim()
                : null,
          },
        },
        onCompleted(response) {
          const result = (
            response as { applyTiktokGoCreator?: { message?: string } }
          ).applyTiktokGoCreator;
          if (result != null && typeof result.message === "string") {
            setError(result.message);
            resolve(false);
            return;
          }
          resolve(true);
        },
        onError() {
          setError("TikTokGOクリエイター申請の保存に失敗しました");
          resolve(false);
        },
      });
    });
  }, [commitApplyTtgo, ttgoApply, ttgoDirty, ttgoUniqueId]);

  const save = useCallback(async () => {
    // TTGO申請ON時はユニークID必須
    if (ttgoDirty && ttgoApply && ttgoUniqueId.trim() === "") {
      setError("TikTokGO ユニークIDを入力してください");
      return;
    }

    await handleSubmit(async ({ tiktokUrl }: InputData) => {
      setLoading(true);
      let saved = false;

      if (changed) {
        await new Promise<void>((resolve) => {
          commit({
            variables: {
              input: { tiktokUrl },
            },
            onCompleted({ updateInfluencerInfo }) {
              if (
                updateInfluencerInfo.__typename === "UpdateInfluencerInfoError"
              ) {
                setError(updateInfluencerInfo.message);
              } else {
                saved = true;
                setChanged(false);
              }
              resolve();
            },
          });
        });
      }

      if (ttgoDirty) {
        const ok = await saveTtgo();
        if (ok) saved = true;
      }

      if (saved) {
        setMessage("保存しました");
      }
      setLoading(false);
    })();
  }, [changed, commit, handleSubmit, saveTtgo, ttgoApply, ttgoDirty, ttgoUniqueId]);

  return (
    <View style={styles.container}>
      <Spacer height={8} />
      <KeyboardAvoidingView>
        <ScrollView>
          <Wrapper>
            <Card style={styles.content}>
              <TextField
                control={control}
                inputAccessoryViewID={KeyboardId}
                label="アカウントURL"
                name="tiktokUrl"
                onChange={(value: string) => {
                  setChanged(value !== profile.tiktokUrl);
                }}
                placeholder="https://www.tiktok.com/@"
                type="url"
              />
              {!isValid && errors.tiktokUrl !== undefined && (
                <ErrorText error={errors.tiktokUrl.message} />
              )}
              <Spacer height={16} />
              <TouchableOpacity
                onPress={() => openURL("https://tiktok.com/")}
                style={styles.open}
              >
                <Text>TikTokを開く</Text>
                <Spacer width={4} />
                <Icon color={Colors.gray} name="outer" size={16} />
              </TouchableOpacity>
            </Card>

            <Spacer height={16} />

            <Card style={styles.content}>
              <TtgoCreatorSwitch
                apply={ttgoApply}
                isTiktokGoCreator={profile.isTiktokGoCreator}
                onChangeApply={setTtgoApply}
                onChangeUniqueId={setTtgoUniqueId}
                tiktokGoStatus={profile.tiktokGoStatus as TtgoStatus}
                uniqueId={ttgoUniqueId}
              />
            </Card>
          </Wrapper>
        </ScrollView>
      </KeyboardAvoidingView>

      <Divider />
      <Card style={styles.footer}>
        <GradientButton
          disabled={!isValid || (!changed && !ttgoDirty)}
          loading={loading}
          onPress={save}
          title="保存する"
        />
      </Card>

      {message !== null && (
        <Toast message={message} onClose={() => setMessage(null)} />
      )}
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
      <InputAccessoryView nativeID={KeyboardId} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  main: {
    flex: 1,
  },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  open: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
});
